import * as React from 'react';

import clsxm from '@/lib/clsxm';

import Col, { ColWidth } from '@/components/col';

import COMMON_CLASS_NAMES from '@/constants/common-class-names';
import { ResponsiveBehavior } from '@/enums/responsive-behavior';
import { RowLayout } from '@/enums/row-layout';
import { VerticalAlignment } from '@/enums/vertical-alignment';

const getColWidths = (layout: RowLayout) => {
  switch (layout) {
    case RowLayout.FULL_WIDE:
    case RowLayout.PAGE_WIDE:
    case RowLayout.MAX_WIDTH_CENTER_WIDE:
    case RowLayout.NARROW_COLUMN_CENTER:
      return [ColWidth.FULL];
    case RowLayout.MAX_WIDTH_CENTER:
      return [ColWidth.MAX_WIDTH_CENTER];
    case RowLayout.TWO_COLUMN_HALFS:
      return [ColWidth.HALF];
    case RowLayout.ONE_COLUMN_WITH_SIDEBAR:
    case RowLayout.ONE_COLUMN_WITH_SIDEBAR_REVERSE:
      return [ColWidth.COLUMN_WITH_SIDEBAR, ColWidth.SIDEBAR];
    case RowLayout.TWO_COLUMN_WIDE_LEFT:
      return [ColWidth.TWO_THIRDS, ColWidth.ONE_THIRD];
    case RowLayout.TWO_COLUMN_WIDE_RIGHT:
      return [ColWidth.ONE_THIRD, ColWidth.TWO_THIRDS];
    case RowLayout.TWO_COLUMN_NARROW_LEFT:
      return [ColWidth.ONE_FOURTH, ColWidth.THREE_FOURTHS];
    case RowLayout.TWO_COLUMN_NARROW_RIGHT:
      return [ColWidth.THREE_FOURTHS, ColWidth.ONE_FOURTH];
    case RowLayout.THREE_COLUMN:
      return [ColWidth.ONE_THIRD];
    case RowLayout.FOUR_COLUMN:
      return [ColWidth.ONE_FOURTH];
    case RowLayout.FIVE_COLUMN:
      return [ColWidth.ONE_FIFTH];
    default:
      return [ColWidth.FULL];
  }
};

const rowClassMap: Partial<Record<RowLayout, string>> = {
  [RowLayout.ONE_COLUMN_WITH_SIDEBAR]: 'flex-col flex-nowrap lg:flex-row',
  [RowLayout.ONE_COLUMN_WITH_SIDEBAR_REVERSE]:
    'flex-col flex-nowrap lg:flex-row-reverse',
};

const rowResponsiveBehaviorClassMap: Partial<
  Record<ResponsiveBehavior, string>
> = {
  [ResponsiveBehavior.STACKED]: 'flex-col lg:flex-row',
  [ResponsiveBehavior.SCROLL]:
    'overflow-x-auto flex-nowrap lg:overflow-visible justify-start lg:justify-center mb-3 lg:-mb-3',
};

const colResponsiveBehaviorClassMap: typeof rowResponsiveBehaviorClassMap = {
  [ResponsiveBehavior.SCROLL]: 'flex-shrink-0 lg:flex-shrink',
};

export type RowProps = {
  slug?: string;
  layout?: RowLayout;
  verticalAlignment?: VerticalAlignment;
  responsiveBehavior?: ResponsiveBehavior;
  className?: string;
  children?: React.ReactNode;
};

const Row = ({
  slug,
  layout = RowLayout.FULL_WIDE,
  responsiveBehavior = ResponsiveBehavior.STACKED,
  verticalAlignment,
  className,
  children,
}: RowProps) => {
  const colWidths = getColWidths(layout);
  const numColWidths = colWidths?.length;
  return (
    <div
      id={slug}
      className={clsxm(
        layout !== RowLayout.PAGE_WIDE && [
          COMMON_CLASS_NAMES.mainContentContainerWidth,
          COMMON_CLASS_NAMES.mainContentContainerHorizontalPadding,
        ],
        'mx-auto -mb-3 flex flex-row flex-wrap',
        rowResponsiveBehaviorClassMap[responsiveBehavior],
        rowClassMap[layout],
        className
      )}
    >
      {React.Children.map(children, (child, i) => {
        //probably a better way to write this
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const imageOrdered =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (child as any)?.type?.name === 'ContentSectionWithImageImage';

        return (
          <Col
            colWidth={colWidths[i % numColWidths]}
            responsiveBehavior={responsiveBehavior}
            className={clsxm(
              verticalAlignment === VerticalAlignment.MIDDLE && 'items-center',
              'col',
              verticalAlignment === VerticalAlignment.BOTTOM && 'items-end',
              layout === RowLayout.MAX_WIDTH_CENTER && 'mx-auto justify-center',
              layout === RowLayout.MAX_WIDTH_CENTER_WIDE &&
                'mx-auto flex max-w-screen-md justify-center',
              layout === RowLayout.NARROW_COLUMN_CENTER &&
                'mx-auto max-w-screen-md px-0 sm:px-3 md:max-w-screen-lg',
              colResponsiveBehaviorClassMap[responsiveBehavior],
              imageOrdered && 'order-first md:order-none'
            )}
          >
            {child}
          </Col>
        );
      })}
    </div>
  );
};
export default Row;
